import React, { useEffect } from 'react';
import usePage from '@monorepo/shared/src/hooks/use-page';
import useOverlay from '@monorepo/shared/src/hooks/use-overlay';
import useTelemetry from '@monorepo/shared/src/hooks/use-telemetry';
import GameProvider, { GameContext } from '@monorepo/shared/src/providers/GameProvider';
import HintProvider from '@monorepo/shared/src/providers/HintProvider';
import OverlayProvider from '@monorepo/shared/src/providers/OverlayProvider';
import ActivitiesLayout from '../components/ActivitiesLayout';
import WithQuery from '../../../../components/layout/WithQuery';
import ProgressProvider from '@monorepo/shared/src/providers/ProgressProvider';
import ZoomProvider from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Zoom/providers/ZoomProvider';
import FindTheObjectActivity from '@monorepo/shared/src/components/activities/FindItActivities/components/FindTheObjectActivity';
import FindTheLetterActivity from '@monorepo/shared/src/components/activities/FindItActivities/components/FindTheLetterActivity';
import FindTheLetterFooter from './components/FindTheLetterFooter';
import FindTheObjectFooter from './components/FindTheObjectFooter';
import NavigationControl from '@monorepo/shared/src/components/modules/Paging/components/NavigationControl';
import ArrowNavigationButton from '@monorepo/shared/src/components/activities/ArrowNavigationButton';
import SharedActivityPlayButton from './components/SharedActivityPlayButton';
import ZoomWrapper from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Zoom/components/ZoomWrapper';
import { useQueryParam, StringParam } from 'use-query-params';
import { ActivityTypes, SharedActivityData } from 'types';
import { ActivityTypes as GameActivityType } from '@monorepo/shared/src/providers/GameProvider/types';
import makeStyles from '@mui/styles/makeStyles';
import { TelemetryEvents } from '@monorepo/shared/src/types';
import { formatCharacter } from '@monorepo/shared/src/util/telemetry-util';

type SharedActivityProps = {
  activityType: ActivityTypes;
  assignmentId: number;
};

export const SharedActivityMap: SharedActivityData = {
  [ActivityTypes.FIND_THE_LETTER]: {
    activity: GameActivityType.FIND_THE_LETTER,
    Footer: FindTheLetterFooter,
    Content: FindTheLetterActivity,
  },
  [ActivityTypes.FIND_THE_OBJECTS]: {
    activity: GameActivityType.FIND_THE_OBJECTS,
    Footer: FindTheObjectFooter,
    Content: FindTheObjectActivity,
  },
};

const ConnectedNavigationControl = (props) => {
  const [{ showOverlay }] = useOverlay();
  return (
    <NavigationControl
      {...props}
      disableNextButton={showOverlay}
      disablePreviousButton={showOverlay}
    />
  );
};

const SharedActivity: React.FC<SharedActivityProps> = ({ activityType, assignmentId }) => {
  const classes = useStyles();
  const [, { fireEvent }] = useTelemetry(TelemetryEvents.ACCESS_ACTIVITY);
  const [, { resetPage }] = usePage();
  const [bookCode] = useQueryParam('bookCode', StringParam);

  const [character, setCharacter] = React.useState(null);

  const letter = bookCode?.slice(-1);
  const sharedActivity = SharedActivityMap[activityType];

  if (!sharedActivity || !letter) return <></>;

  const { activity, Footer, Content } = sharedActivity;

  useEffect(() => {
    return () => resetPage();
  }, []);

  useEffect(() => {
    if (activity && bookCode && character) {
      fireEvent({
        activityType: activity,
        bookCode,
        character: formatCharacter(character),
      });
    }
  }, [activity, bookCode, character]);

  return (
    <GameProvider character={letter} activityType={activity} isStudentApp={true}>
      <GameContext.Consumer>
        {([
          { activityData, isRestarting, loading },
          { handleCurrentPageCompletionWithAlert, handleTurnActivityPage },
        ]) => {
          if (activityData?.book) {
            const { characters } = activityData.book;
            setCharacter(characters?.[0]);
          }

          return (
            <WithQuery loading={loading}>
              <ProgressProvider handlePageCompletion={handleCurrentPageCompletionWithAlert}>
                <OverlayProvider>
                  <HintProvider>
                    <ZoomProvider>
                      <ActivitiesLayout
                        assignmentId={assignmentId}
                        footerProps={{ children: Footer && <Footer /> }}
                        gutterBottom
                      >
                        <ConnectedNavigationControl
                          handleContinue={handleTurnActivityPage}
                          ArrowNavigationButton={ArrowNavigationButton}
                        />
                        <div className={classes.wrapper}>
                          {!isRestarting && (
                            <ZoomWrapper>
                              <Content />
                            </ZoomWrapper>
                          )}
                          <SharedActivityPlayButton />
                        </div>
                      </ActivitiesLayout>
                    </ZoomProvider>
                  </HintProvider>
                </OverlayProvider>
              </ProgressProvider>
            </WithQuery>
          );
        }}
      </GameContext.Consumer>
    </GameProvider>
  );
};

const useStyles = makeStyles(() => ({
  wrapper: {
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export default SharedActivity;
